import { Button, type ButtonProps } from "@chakra-ui/react";
import { forwardRef } from "react";

export type ToolbarButtonProps = Omit<ButtonProps, "aria-pressed"> & {
  /**
   * Accessible name. Only needed where the visible content is an icon or a single letter.
   * Nullable so `t(…)` can be passed straight in.
   */
  label?: string | null;
  /**
   * Set for toggles (bold, lists, …) — it renders `aria-pressed` and the active background.
   * Leave unset for actions (insert table, insert template, …), which are not toggles.
   */
  isPressed?: boolean;
};

/**
 * A button in the editor toolbar: a native button, part of the roving tabindex
 * (see `useRovingToolbar`), with an accessible name and — when it is a toggle — a state that is
 * announced rather than only coloured.
 */
export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(({ label, isPressed, ...props }, ref) => (
  <Button
    ref={ref}
    size="xs"
    data-toolbar-item=""
    tabIndex={-1}
    aria-label={label ?? undefined}
    aria-pressed={isPressed}
    backgroundColor={isPressed ? "smBackgroundTertiary" : undefined}
    {...props}
  />
));
ToolbarButton.displayName = "ToolbarButton";
